import React from "react";
import styled from "styled-components";
import { Tag } from "../../../../../design-system";
import { TrendReport } from "../../../../../database/community/contentEngine";
import { Card, CardTitle, CardHint, ExternalLink, Muted, Row, RowList, Spacer } from "../shared";
import { compact, LabeledOutlier, NicheRow } from "./homeDerive";

const Split = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-4);
`;

const BlockTitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const SelfRow = styled(Row)<{ $self: boolean }>`
  background: ${(p) => (p.$self ? "var(--color-surface-subtle)" : "transparent")};
  border-radius: ${(p) => (p.$self ? "var(--radius-sm)" : "0")};
  padding-left: ${(p) => (p.$self ? "6px" : "0")};
  padding-right: ${(p) => (p.$self ? "6px" : "0")};
`;

const Account = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OutlierTitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export type NichePanelProps = {
  reports: TrendReport[];
  ranking: NicheRow[];
  outliers: LabeledOutlier[];
};

export const NichePanel: React.FC<NichePanelProps> = ({
  reports,
  ranking,
  outliers,
}) => {
  const latest = reports[0];
  if (!latest) return null;
  const sources = Array.from(
    new Set(
      reports
        .map((r) => r.source)
        .filter((s): s is string => typeof s === "string" && s.length > 0)
    )
  );

  return (
    <Card>
      <CardTitle>
        Where you stand in your niche
        {latest.niche && <Tag variant="neutral" size="sm">{latest.niche}</Tag>}
      </CardTitle>
      <Split>
        {ranking.length > 0 && (
          <div>
            <BlockTitle>Average views per account</BlockTitle>
            <RowList>
              {ranking.map((r, i) => (
                <SelfRow key={r.account} $self={r.isSelf}>
                  <Muted style={{ minWidth: 14 }}>{i + 1}</Muted>
                  {r.topUrl ? (
                    <ExternalLink
                      href={r.topUrl}
                      style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      @{r.account}
                    </ExternalLink>
                  ) : (
                    <Account>@{r.account}</Account>
                  )}
                  {r.isSelf && (
                    <Tag variant="accent" size="sm">
                      You
                    </Tag>
                  )}
                  {r.platform && <Muted>{r.platform}</Muted>}
                  <Spacer />
                  <Muted>
                    {r.avgViews !== null ? `${compact(r.avgViews)} avg` : ""}
                    {r.topViews !== null
                      ? ` · ${compact(r.topViews)} top`
                      : ""}
                  </Muted>
                </SelfRow>
              ))}
            </RowList>
          </div>
        )}
        {outliers.length > 0 && (
          <div>
            <BlockTitle>Researched outlier posts</BlockTitle>
            <RowList>
              {outliers.map((o, i) => (
                <Row key={`${o.url || i}`}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    {o.url ? (
                      <ExternalLink href={o.url}>
                        <OutlierTitle>
                          {o.title || o.account || "Untitled post"}
                        </OutlierTitle>
                      </ExternalLink>
                    ) : (
                      <OutlierTitle>
                        {o.title || o.account || "Untitled post"}
                      </OutlierTitle>
                    )}
                    <Muted>
                      {[
                        o.account && `@${o.account.replace(/^@/, "")}`,
                        typeof o.views === "number" &&
                          `${compact(o.views)} views`,
                        typeof o.saveRate === "number" &&
                          `${(o.saveRate * 100).toFixed(1)}% saves`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Muted>
                  </div>
                  {o.platform && (
                    <Tag variant="neutral" size="sm">
                      {o.platform}
                    </Tag>
                  )}
                </Row>
              ))}
            </RowList>
          </div>
        )}
      </Split>
      <CardHint>
        From trend report{reports.length === 1 ? "" : "s"}{" "}
        {reports.map((r) => r.id).join(", ")}
        {sources.length > 0 && ` · sources: ${sources.join(", ")}`}
        {typeof latest.analyzedCount === "number" &&
          ` · ${latest.analyzedCount} posts analyzed`}
      </CardHint>
    </Card>
  );
};

export default NichePanel;
